/*
 * NOTE
 * The shelter's two-letter country code, held outside the session store for the same reason as
 * `activeDateFormat`: helpers stay pure, so `phone.helpers` and the form validator can never import
 * the session store. The store WRITES here (`setActiveShelterCountry`), everything else READS.
 *
 * NO COUNTRY MEANS US. A missing or malformed value falls back to `'US'`, the same fallback every
 * shelter reader in api, adopt and foster uses — so an unconfigured shelter keeps today's US mask,
 * US display and texting.
 *
 * Two lists on purpose — they start equal but drift apart, so never merge them:
 * - `MASKED_PHONE_COUNTRIES` — who gets the US phone mask and US display. Should never grow.
 * - `SMS_COUNTRIES` — who may be sent texts. Grows as texting opens per country ("NZ" next).
 *   Mirrors `PhoneNumberHelper::SMS_COUNTRIES` in api and the list in foster's server; the api
 *   refuses a text for any other country regardless of what this screen shows.
 *
 * Seeded SYNCHRONOUSLY at module load from the persisted profile, and a `ref` so shelter switching
 * (`AActionLoginAs`) takes effect without a reload.
 * */

import { ref } from 'vue';
import { readPersistedShelterProfile } from './shelterProfileStorage';

const DEFAULT_SHELTER_COUNTRY = 'US';
const MASKED_PHONE_COUNTRIES = ['US', 'CA'];
const SMS_COUNTRIES = ['US', 'CA'];

const activeShelterCountry = ref<string>(getPersistedShelterCountry());

// HELPERS
function normalizeCountry(country: unknown): string {
  const code = typeof country === 'string' ? country.trim().toUpperCase() : '';

  return /^[A-Z]{2}$/.test(code) ? code : DEFAULT_SHELTER_COUNTRY;
}

function getPersistedShelterCountry(): string {
  const shelterProfile = readPersistedShelterProfile();

  return normalizeCountry(shelterProfile?.country);
}

function getActiveShelterCountry(): string {
  return activeShelterCountry.value;
}

function isMaskedPhoneCountry(country?: string | null): boolean {
  return MASKED_PHONE_COUNTRIES.includes(normalizeCountry(country ?? activeShelterCountry.value));
}

function canSendSms(country?: string | null): boolean {
  return SMS_COUNTRIES.includes(normalizeCountry(country ?? activeShelterCountry.value));
}

// EVENTS
function setActiveShelterCountry(country: unknown) {
  activeShelterCountry.value = normalizeCountry(country);
}

function resetActiveShelterCountry() {
  activeShelterCountry.value = DEFAULT_SHELTER_COUNTRY;
}

export {
  canSendSms,
  getActiveShelterCountry,
  isMaskedPhoneCountry,
  MASKED_PHONE_COUNTRIES,
  resetActiveShelterCountry,
  setActiveShelterCountry,
  SMS_COUNTRIES,
};
